import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useAuth } from "@/context/AuthContext";
import { Heart, Minus, Plus, Truck, RotateCcw, Sparkles, Star, Camera } from "lucide-react";
import ProductCard from "@/components/ProductCard";
import NotifyMe from "@/components/NotifyMe";
import { toast } from "sonner";
import { whatsappLink, WhatsAppIcon } from "@/lib/whatsapp";

function rupee(n) { return `₹${Number(n).toLocaleString("en-IN")}`; }

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [size, setSize] = useState("");
  const [qty, setQty] = useState(1);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: "", image_url: "" });
  const [uploading, setUploading] = useState(false);
  const { addToCart } = useCart();
  const { isWishlisted, toggle } = useWishlist();
  const { user } = useAuth();

  // Memoise reviews that have an attached client photo — avoids recomputing on
  // every keystroke when the user is typing a new review.
  const photoReviews = useMemo(
    () => reviews.filter((r) => r.image_url),
    [reviews],
  );

  useEffect(() => {
    setProduct(null);
    api.get(`/products/${id}`).then(({ data }) => {
      setProduct(data);
      setSize(data.sizes?.[0] || "Free Size");
      setActiveImg(0);
      api.get(`/products`, { params: { category: data.category, limit: 8 } })
        .then(({ data: rel }) => setRelated(rel.filter((p) => p.id !== data.id).slice(0, 4)));
    });
    api.get(`/reviews/${id}`).then(({ data }) => setReviews(data));
  }, [id]);

  if (!product) return <div className="px-6 md:px-24 py-24 text-stone-500">Loading...</div>;

  const wl = isWishlisted(product.id);
  const dmForPrice = !product.price || product.price === 0;

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Please sign in to review"); return; }
    await api.post("/reviews", {
      product_id: product.id,
      rating: reviewForm.rating,
      comment: reviewForm.comment,
      image_url: reviewForm.image_url || null,
    });
    toast.success("Review added");
    setReviewForm({ rating: 5, comment: "", image_url: "" });
    const { data: r } = await api.get(`/reviews/${id}`);
    setReviews(r);
    const { data: p } = await api.get(`/products/${id}`);
    setProduct(p);
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    if (!user) { toast.error("Please sign in to upload a photo"); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/uploads", form, { headers: { "Content-Type": "multipart/form-data" } });
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      setReviewForm((f) => ({ ...f, image_url: `${backend}${data.url}` }));
      toast.success("Photo attached");
    } catch (err) {
      toast.error("Could not upload photo");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <div className="px-6 md:px-12 lg:px-24 pt-10 text-xs text-stone-500 tracking-wide">
        <Link to="/" className="hover:text-[#D4AF37]">Home</Link>
        <span className="mx-2">/</span>
        <Link to={`/shop/${product.category}`} className="hover:text-[#D4AF37] capitalize">{product.category}</Link>
        <span className="mx-2">/</span>
        <span className="text-stone-700">{product.name}</span>
      </div>

      <section className="px-6 md:px-12 lg:px-24 pt-8 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-10" data-testid="product-detail">
        <div className="lg:col-span-7">
          <div className="bg-[#F3EAE8] hover-zoom aspect-[4/5] max-h-[780px]">
            <img src={product.images[activeImg]} alt={product.name} className="w-full h-full object-cover" />
          </div>
          {product.images.length > 1 && (
            <div className="mt-4 flex gap-3 overflow-x-auto">
              {product.images.map((src, i) => (
                <button key={src} onClick={() => setActiveImg(i)}
                        className={`w-20 h-24 flex-shrink-0 border ${i === activeImg ? "border-[#D4AF37]" : "border-warm"}`}>
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-5 lg:pl-6">
          <div className="text-[11px] tracking-overline uppercase text-stone-500 capitalize">{product.category}</div>
          <h1 className="font-serif-h font-light text-4xl md:text-5xl text-stone-900 mt-2 leading-tight">{product.name}</h1>

          {product.review_count > 0 && (
            <div className="flex items-center gap-2 mt-3 text-sm text-stone-600">
              <div className="flex">
                {[1,2,3,4,5].map((s) => (
                  <Star key={s} size={14} fill={s <= Math.round(product.rating) ? "#D4AF37" : "none"} color="#D4AF37" strokeWidth={1.2} />
                ))}
              </div>
              <span>{product.rating} ({product.review_count})</span>
            </div>
          )}

          <div className="flex items-baseline gap-4 mt-6">
            {dmForPrice ? (
              <div className="text-[11px] tracking-overline uppercase text-[#5A1618]">Price on Enquiry</div>
            ) : (
              <>
                <div className="text-2xl text-stone-900">{rupee(product.price)}</div>
                {product.compare_at_price && (
                  <div className="text-stone-400 line-through">{rupee(product.compare_at_price)}</div>
                )}
                {product.compare_at_price && (
                  <div className="text-[11px] tracking-overline uppercase text-[#D4AF37]">
                    Save {rupee(product.compare_at_price - product.price)}
                  </div>
                )}
              </>
            )}
          </div>
          <div className="text-xs text-stone-500 mt-1">{dmForPrice ? "Message us on WhatsApp for pricing & availability" : "Inclusive of all taxes"}</div>

          <p className="mt-8 text-stone-700 font-light leading-relaxed">{product.description}</p>

          <div className="mt-10">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[11px] tracking-overline uppercase text-stone-700">Size</div>
              <button className="text-[11px] tracking-overline uppercase text-[#D4AF37]">Size Guide</button>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="size-options">
              {product.sizes.map((s) => (
                <button key={s} onClick={() => setSize(s)} data-testid={`size-${s}`}
                        className={`min-w-[56px] px-4 py-3 border text-sm transition ${size === s ? "border-[#1C1917] bg-[#1C1917] text-white" : "border-warm text-stone-700 hover:border-[#D4AF37]"}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-8 flex items-center gap-4">
            {!dmForPrice && product.stock > 0 && (
              <div className="flex items-center border border-warm">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-12 flex items-center justify-center" data-testid="qty-dec"><Minus size={14}/></button>
                <div className="w-10 text-center text-sm" data-testid="qty-val">{qty}</div>
                <button onClick={() => setQty(qty + 1)} className="w-10 h-12 flex items-center justify-center" data-testid="qty-inc"><Plus size={14}/></button>
              </div>
            )}
            <div className="text-xs text-stone-500">{product.stock > 0 ? `${product.stock} in stock` : "Out of stock"}</div>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            {dmForPrice ? (
              <a
                href={whatsappLink(product.name)}
                target="_blank"
                rel="noreferrer"
                data-testid="whatsapp-dm-btn"
                className="flex-1 inline-flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#1ebe5a] text-white px-8 py-4 rounded-sm font-medium tracking-wide text-sm uppercase transition-colors"
              >
                <WhatsAppIcon size={18} /> DM for Price on WhatsApp
              </a>
            ) : (
              <button onClick={() => addToCart(product.id, size, qty)}
                      disabled={product.stock === 0}
                      data-testid="add-to-cart-btn"
                      className="btn-ink flex-1 justify-center disabled:opacity-50">
                Add to Bag
              </button>
            )}
            <button onClick={() => toggle(product)} data-testid="product-wishlist-btn"
                    className="btn-gold-outline justify-center">
              <Heart size={14} fill={wl ? "#D4AF37" : "none"} /> {wl ? "Saved" : "Wishlist"}
            </button>
          </div>

          {!dmForPrice && product.stock === 0 && <NotifyMe productId={product.id} />}

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6 text-xs text-stone-600">
            <div className="flex gap-3"><Truck size={18} strokeWidth={1.2} className="text-[#D4AF37]"/><div><div className="font-medium">Free Shipping</div><div>Above ₹5,000</div></div></div>
            <div className="flex gap-3"><RotateCcw size={18} strokeWidth={1.2} className="text-[#D4AF37]"/><div><div className="font-medium">Easy Returns</div><div>Within 7 days</div></div></div>
            <div className="flex gap-3"><Sparkles size={18} strokeWidth={1.2} className="text-[#D4AF37]"/><div><div className="font-medium">Handcrafted</div><div>In small batches</div></div></div>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="px-6 md:px-12 lg:px-24 py-16 border-t border-warm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-[11px] tracking-overline uppercase text-stone-500 mb-3">Voices</div>
            <h2 className="font-serif-h font-light text-3xl md:text-4xl text-stone-900">Reviews</h2>
            <p className="mt-4 text-stone-600 font-light text-sm">Honest words from women who wear Riddhi.</p>

            {user && (
              <form onSubmit={submitReview} className="mt-8 border border-warm p-6 bg-white rounded-sm" data-testid="review-form">
                <div className="flex gap-1 mb-4">
                  {[1,2,3,4,5].map((s) => (
                    <button type="button" key={s} onClick={() => setReviewForm({ ...reviewForm, rating: s })}>
                      <Star size={20} fill={s <= reviewForm.rating ? "#D4AF37" : "none"} color="#D4AF37" strokeWidth={1.2}/>
                    </button>
                  ))}
                </div>
                <textarea placeholder="Share your thoughts..." value={reviewForm.comment}
                          onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                          className="w-full bg-transparent border-b border-warm focus:border-[#D4AF37] outline-none py-2 text-sm min-h-[80px]"
                          data-testid="review-comment" required />

                {reviewForm.image_url && (
                  <div className="mt-4 relative inline-block">
                    <img src={reviewForm.image_url} alt="" className="h-24 w-24 object-cover border border-warm rounded-sm" />
                    <button type="button" onClick={() => setReviewForm({ ...reviewForm, image_url: "" })}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-white border border-warm rounded-full text-xs"
                            data-testid="remove-review-photo">×</button>
                  </div>
                )}

                <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                  <label className="inline-flex items-center gap-2 text-[11px] tracking-overline uppercase text-[#D4AF37] cursor-pointer hover:opacity-80"
                         data-testid="upload-review-photo">
                    <Camera size={14} strokeWidth={1.4} />
                    {uploading ? "Uploading..." : reviewForm.image_url ? "Change Photo" : "Add Photo"}
                    <input type="file" accept="image/*" className="hidden"
                           onChange={(e) => uploadPhoto(e.target.files?.[0])} disabled={uploading} />
                  </label>
                  <button className="btn-ink" data-testid="submit-review" disabled={uploading}>Post Review</button>
                </div>
                <div className="mt-2 text-[10px] text-stone-400">Upload a photo wearing your Riddhi piece (JPG/PNG, up to 5 MB)</div>
              </form>
            )}
          </div>

          <div className="lg:col-span-7 space-y-6">
            {reviews.length === 0 && <div className="text-sm text-stone-500">No reviews yet. Be the first to share.</div>}

            {/* Client photo gallery strip */}
            {photoReviews.length > 0 && (
              <div className="mb-6">
                <div className="text-[11px] tracking-overline uppercase text-stone-500 mb-3">Styled by our Clients</div>
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {photoReviews.map((r) => (
                    <a key={`g-${r.id}`} href={r.image_url} target="_blank" rel="noreferrer"
                       className="flex-shrink-0 relative block hover-zoom" data-testid={`client-photo-${r.id}`}>
                      <img src={r.image_url} alt={`by ${r.user_name}`} className="w-28 h-36 object-cover bg-[#F3EAE8]" />
                      <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1">
                        <div className="text-[10px] text-white font-medium truncate">{r.user_name}</div>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {reviews.map((r) => (
              <div key={r.id} className="border-b border-warm pb-6">
                <div className="flex items-center justify-between">
                  <div className="font-medium text-stone-900">{r.user_name}</div>
                  <div className="flex">
                    {[1,2,3,4,5].map((s) => (
                      <Star key={s} size={13} fill={s <= r.rating ? "#D4AF37" : "none"} color="#D4AF37" strokeWidth={1.2}/>
                    ))}
                  </div>
                </div>
                {r.image_url && (
                  <a href={r.image_url} target="_blank" rel="noreferrer" className="block mt-3">
                    <img src={r.image_url} alt="" className="max-w-[220px] max-h-[280px] object-cover border border-warm rounded-sm" />
                  </a>
                )}
                <p className="mt-2 text-stone-600 font-light text-sm leading-relaxed">{r.comment}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="px-6 md:px-12 lg:px-24 py-16">
          <h2 className="font-serif-h font-light text-3xl md:text-4xl text-stone-900 mb-10">You may also love</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {related.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}
    </div>
  );
}
